import { useState } from 'react'

import { t } from '../../../../../../common/i18n.js'
import { Language } from '../../../../../../common/types/i18n.js'
import { Button } from '../../../../../components/button/button.js'
import { Select } from '../../../../../components/forms/select/select.js'
import { TextField } from '../../../../../components/forms/text-field/text-field.js'
import { Tag } from '../../../../../components/icon/solar-icons.js'
import { FieldRow, Section, SectionHeader } from '../../../../../components/section/section.js'
import type { CustomFieldWithValue } from '../employee-profile-types.js'
import '../employee-profile-view.css'

interface CustomFieldsSectionProps {
    customFields: CustomFieldWithValue[]
    lang: Language
    onSaveCustomFields?: (values: Record<number, string | null>) => Promise<boolean>
}

// List-like free-text fields (e.g. Clifton top 5, granted accesses) are edited
// as a plain textarea and displayed with their line breaks preserved.
// 'longText' is the Humaans-imported spelling of the same type.
const isMultilineType = (fieldType: string) => fieldType === 'textarea' || fieldType === 'longText'

/**
 * The single "Lisaväljad" (custom fields) section. The GET filters defs by
 * viewer visibility and stamps a per-field `can_edit` (custom-field admin, or
 * self on a public field); editing gates on that. Owns its own edit-mode state.
 */
export const CustomFieldsSection = ({
    customFields,
    lang,
    onSaveCustomFields,
}: CustomFieldsSectionProps) => {
    const ph = t.portal
    const canEditField = (f: CustomFieldWithValue) => f.can_edit === true
    const canEdit = customFields.some(canEditField)
    const [isEditing, setIsEditing] = useState(false)
    const [editData, setEditData] = useState<Record<number, string>>({})
    const [saving, setSaving] = useState(false)

    // Viewers see only fields that hold a value; self/admin see every returned
    // field so they can fill empties.
    const visibleFields = canEdit ? customFields : customFields.filter((f) => f.value)
    if (visibleFields.length === 0) return null

    return (
        <Section>
            <SectionHeader
                icon={Tag}
                title={ph.profile.customFieldsSection(lang)}
                actions={
                    canEdit &&
                    onSaveCustomFields &&
                    !isEditing && (
                        <Button
                            variant="secondary"
                            size="small"
                            text={ph.profile.editProfile(lang)}
                            onClick={() => {
                                const initial: Record<number, string> = {}
                                for (const f of customFields) {
                                    initial[f.id] = f.value ?? ''
                                }
                                setEditData(initial)
                                setIsEditing(true)
                            }}
                        />
                    )
                }
            />
            {isEditing ? (
                <div className="portal-profile__edit-form">
                    {customFields.map((f) =>
                        canEditField(f) ? (
                            <div key={f.id} className="portal-profile__edit-field">
                                <label className="portal-profile__field-label">{f.name}</label>
                                {f.field_type === 'select' && f.options ? (
                                    <Select
                                        placeholder="—"
                                        value={editData[f.id] ?? ''}
                                        onChange={(value) =>
                                            setEditData((prev) => ({ ...prev, [f.id]: value }))
                                        }
                                        options={f.options
                                            .split(',')
                                            .map((opt) => opt.trim())
                                            .filter(Boolean)
                                            .map((opt) => ({ value: opt, label: opt }))}
                                    />
                                ) : isMultilineType(f.field_type) ? (
                                    <TextField
                                        as="textarea"
                                        textAreaProps={{ rows: 4 }}
                                        value={editData[f.id] ?? ''}
                                        onChange={(value) =>
                                            setEditData((prev) => ({ ...prev, [f.id]: value }))
                                        }
                                    />
                                ) : (
                                    <TextField
                                        inputProps={{
                                            type:
                                                f.field_type === 'number'
                                                    ? 'number'
                                                    : f.field_type === 'date'
                                                      ? 'date'
                                                      : 'text',
                                        }}
                                        value={editData[f.id] ?? ''}
                                        onChange={(value) =>
                                            setEditData((prev) => ({ ...prev, [f.id]: value }))
                                        }
                                    />
                                )}
                            </div>
                        ) : (
                            // Admin-only fields (granted accesses, old profile) stay read-only for self.
                            <FieldRow
                                key={f.id}
                                label={f.name}
                                multiline={isMultilineType(f.field_type)}
                            >
                                {f.value ?? '—'}
                            </FieldRow>
                        ),
                    )}
                    <div className="portal-profile__edit-actions">
                        <Button
                            text={
                                saving
                                    ? ph.profile.savingProfile(lang)
                                    : ph.profile.saveChanges(lang)
                            }
                            size="small"
                            isDisabled={saving}
                            onClick={async () => {
                                if (!onSaveCustomFields) return
                                setSaving(true)
                                // Only submit fields the actor may edit.
                                const values: Record<number, string | null> = {}
                                for (const f of customFields) {
                                    if (!canEditField(f)) continue
                                    values[f.id] = editData[f.id]?.trim() || null
                                }
                                const ok = await onSaveCustomFields(values)
                                setSaving(false)
                                if (ok) setIsEditing(false)
                            }}
                        />
                        <Button
                            text={ph.profile.cancelEdit(lang)}
                            variant="secondary"
                            size="small"
                            onClick={() => setIsEditing(false)}
                        />
                    </div>
                </div>
            ) : (
                visibleFields.map((f) => (
                    <FieldRow key={f.id} label={f.name} multiline={isMultilineType(f.field_type)}>
                        {f.value ?? '—'}
                    </FieldRow>
                ))
            )}
        </Section>
    )
}
